// Port of SuiteModel/MapArrange.swift: arranging a map when the writer asks — stacks, regions, Arrange Around Centre and
// Tidy Branch, the same rules and numbers. Each returns new places; nothing here writes. (The Swift extends `MapLayout`;
// here they're `MapArrange`, beside it.)
import { between } from '../automerge/fractionalIndex.ts';
import { swiftCompare } from '../model/order.ts';
import { MapLayout, type MapFrame, type MapFrames } from './layout.ts';
import { mapNoteOf, type MapContent, type MapLine, type MapOutline, type MapPoint, type MapRegion } from './map.ts';

const maxX = (f: MapFrame) => f.x + f.width;
const maxY = (f: MapFrame) => f.y + f.height;
const midX = (f: MapFrame) => f.x + f.width / 2;
const midY = (f: MapFrame) => f.y + f.height / 2;

/** Swift's lexicographic tuple `<` over numbers and then an id. */
function tupleCompare(a: readonly (number | string)[], b: readonly (number | string)[]): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const x = a[i], y = b[i];
    const d = typeof x === 'number' && typeof y === 'number' ? (x < y ? -1 : x > y ? 1 : 0) : swiftCompare(String(x), String(y));
    if (d !== 0) return d;
  }
  return a.length - b.length;
}

/** Swift's `max()` of Strings. */
function maxString(values: string[]): string | undefined {
  let best: string | undefined;
  for (const v of values) if (best === undefined || swiftCompare(v, best) > 0) best = v;
  return best;
}

export const MapArrange = {
  /** The room between notes in a stack. */
  stackGap: 12,
  /** The room a region leaves round the notes it was made around (more above, for its label). */
  regionMargin: { side: 28, top: 40 } as const,

  // MARK: Stacks

  /** Each stack's head and the notes under it, in order. A note under a note that's itself in a stack belongs to that
   *  stack's head; a loop of stacks is ignored. */
  stacks(content: MapContent): Map<string, string[]> {
    const byID = new Map<string, MapContent['notes'][number]>();
    for (const n of content.notes) if (!byID.has(n.id)) byID.set(n.id, n);
    const head = (note: MapContent['notes'][number]): string | null => {
      const seen = new Set([note.id]);
      let current = note;
      while (current.stack !== null) {
        const next = byID.get(current.stack);
        if (!next) break;
        if (seen.has(current.stack)) return null;
        seen.add(current.stack);
        current = next;
      }
      return current.id === note.id ? null : current.id;
    };
    const members = new Map<string, MapContent['notes']>();
    for (const note of content.notes) {
      const h = head(note);
      if (h !== null) members.set(h, [...(members.get(h) ?? []), note]);
    }
    const result = new Map<string, string[]>();
    for (const [h, notes] of members) {
      result.set(h, [...notes].sort((a, b) => tupleCompare([a.stackOrder ?? '', a.id], [b.stackOrder ?? '', b.id])).map((n) => n.id));
    }
    return result;
  },

  /** How far a note's outline reaches above and below its words, so stacked notes' outlines don't overlap. */
  outlineReach(outline: MapOutline): number {
    switch (outline) {
      case 'none': return 0;
      case 'underline': return 4;
      case 'box': return 8;
      case 'oval': return 14;
      case 'cloud': return 24;
    }
  },

  /** Where the notes in stacks are: in a column under their head, at its left edge, `stackGap` apart outline to outline.
   *  `frames` are where the notes would be on their own (members' own `at` is ignored). */
  stackPlaces(content: MapContent, frames: MapFrames): Map<string, MapPoint> {
    const places = new Map<string, MapPoint>();
    const reach = (id: string) => MapArrange.outlineReach(mapNoteOf(content, id)?.outline ?? 'none');
    for (const [head, members] of MapArrange.stacks(content)) {
      const top = frames.get(head);
      if (!top) continue;
      let y = maxY(top), above = head;
      for (const id of members) {
        y += reach(above) + MapArrange.stackGap + reach(id);
        places.set(id, { x: top.x, y });
        y += frames.get(id)?.height ?? 0;
        above = id;
      }
    }
    return places;
  },

  /** Whether a line joins two notes of one stack (it isn't drawn: the stack shows they belong together). */
  isWithinStack(line: Pick<MapLine, 'from' | 'to'>, stacks: ReadonlyMap<string, readonly string[]>): boolean {
    for (const [head, members] of stacks) {
      const all = new Set([...members, head]);
      if (all.has(line.from) && all.has(line.to)) return true;
    }
    return false;
  },

  /** Frames with every stacked note moved under its head. */
  stacked(frames: MapFrames, content: MapContent): Map<string, MapFrame> {
    const result = new Map([...frames].map(([id, f]) => [id, { ...f }]));
    for (const [id, point] of MapArrange.stackPlaces(content, frames)) {
      const f = result.get(id);
      if (f) result.set(id, { ...f, x: point.x, y: point.y });
    }
    return result;
  },

  /** Stack: the selection in one column under a head — a stack's head when one of them is in a stack already, else the
   *  note nearest the top. The others follow the head's own notes, top to bottom. Null when fewer than two notes. */
  stacking(ids: Iterable<string>, content: MapContent, frames: MapFrames): { head: string; members: { id: string; order: string }[] } | null {
    const existing = MapArrange.stacks(content);
    const headOf = new Map<string, string>();
    for (const [head, members] of existing) for (const m of members) headOf.set(m, head);
    const place = (id: string) => [frames.get(id)?.y ?? 0, frames.get(id)?.x ?? 0, id] as const;
    const chosen = [...new Set(ids)].filter((id) => mapNoteOf(content, id) !== null).sort((a, b) => tupleCompare(place(a), place(b)));
    if (chosen.length <= 1) return null;
    let head = chosen[0];
    for (const id of chosen) {
      const h = existing.has(id) ? id : headOf.get(id);
      if (h !== undefined) { head = h; break; }
    }
    const kept = [...(existing.get(head) ?? [])];
    const orders = new Map<string, string>();
    for (const n of content.notes) if (kept.includes(n.id)) orders.set(n.id, n.stackOrder ?? '');
    const adding: string[] = [];
    for (const id of chosen) {
      if (id === head || kept.includes(id)) continue;
      adding.push(id);
      for (const member of existing.get(id) ?? []) if (!kept.includes(member) && !adding.includes(member)) adding.push(member);
    }
    let last = maxString(kept.flatMap((id) => orders.get(id) ?? []));
    for (const id of adding) {
      const order = between(last, undefined);
      orders.set(id, order);
      last = order;
      kept.push(id);
    }
    return { head, members: kept.map((id) => ({ id, order: orders.get(id) ?? '' })) };
  },

  // MARK: Regions

  /** A region round these frames, with room for its label above them. */
  regionFrame(frames: readonly MapFrame[]): MapFrame | null {
    const first = frames[0];
    if (!first) return null;
    let x0 = first.x, y0 = first.y, x1 = maxX(first), y1 = maxY(first);
    for (const f of frames.slice(1)) {
      x0 = Math.min(x0, f.x); y0 = Math.min(y0, f.y); x1 = Math.max(x1, maxX(f)); y1 = Math.max(y1, maxY(f));
    }
    const m = MapArrange.regionMargin;
    return { x: x0 - m.side, y: y0 - m.top, width: x1 - x0 + m.side * 2, height: y1 - y0 + m.top + m.side };
  },

  /** `notes(in:frames:)`: the notes lying in a region (their middles inside it): what moves with it. */
  notesInRegion(region: Pick<MapRegion, 'at' | 'width' | 'height'>, frames: MapFrames): string[] {
    return [...frames].filter(([, f]) =>
      midX(f) >= region.at.x && midX(f) <= region.at.x + region.width && midY(f) >= region.at.y && midY(f) <= region.at.y + region.height,
    ).map(([id]) => id).sort(swiftCompare);
  },

  // MARK: Arrange Around Centre

  /** The ring the centre's own notes sit on starts this far beyond the centre's edge; each further ring this much further. */
  firstRing: 150,
  ringGap: 170,
  /** Room between notes on a ring. */
  ringSpacing: 30,

  /** The notes joined to the centre laid out round it in rings, one ring per step from the centre, each branch given a
   *  share of the circle by how many notes it ends in. Branches keep their order round the centre, starting where the
   *  first one (clockwise from the top) already is. Notes not joined to the centre, and stacked notes (they follow their
   *  head), stay where they are; so does the centre. */
  arrangeAroundCentre(content: MapContent, frames: MapFrames): Map<string, MapPoint> {
    const centre = content.centre;
    const middle = centre === null ? undefined : frames.get(centre);
    if (centre === null || !middle) return new Map();
    const stacked = new Set([...MapArrange.stacks(content).values()].flat());
    const parents = [...MapLayout.parents(content)].filter(([child]) => !stacked.has(child) && frames.has(child));
    const cx = midX(middle), cy = midY(middle);

    /** Clockwise from the top, 0 ..< 2π. */
    const angle = (id: string): number => {
      const f = frames.get(id);
      if (!f) return 0;
      let a = Math.atan2(midY(f) - cy, midX(f) - cx) + Math.PI / 2;
      if (a < 0) a += 2 * Math.PI;
      if (a >= 2 * Math.PI) a -= 2 * Math.PI;
      return a;
    };
    const children = new Map<string, string[]>();
    for (const [child, parent] of parents) children.set(parent, [...(children.get(parent) ?? []), child]);
    for (const [key, kids] of children) children.set(key, kids.sort((a, b) => tupleCompare([angle(a), a], [angle(b), b])));

    const weights = new Map<string, number>();
    const weight = (id: string): number => {
      const known = weights.get(id);
      if (known !== undefined) return known;
      const w = Math.max(1, (children.get(id) ?? []).reduce((sum, kid) => sum + weight(kid), 0));
      weights.set(id, w);
      return w;
    };
    const depth = new Map<string, number>([[centre, 0]]);
    const queue = [centre];
    for (let index = 0; index < queue.length; index++) {
      const id = queue[index];
      for (const child of children.get(id) ?? []) {
        depth.set(child, (depth.get(id) ?? 0) + 1);
        queue.push(child);
      }
    }
    const deepest = Math.max(0, ...depth.values());
    if (!(deepest > 0)) return new Map();

    // Each ring as far out as it must be for its notes to fit round it.
    const radii = [0];
    for (let d = 1; d <= deepest; d++) {
      let around = 0;
      for (const [id, dd] of depth) if (dd === d) around += (frames.get(id)?.width ?? 0) + MapArrange.ringSpacing;
      const base = d === 1 ? Math.max(middle.width, middle.height) / 2 + MapArrange.firstRing : radii[d - 1] + MapArrange.ringGap;
      radii.push(Math.max(base, around / (2 * Math.PI)));
    }

    const places = new Map<string, MapPoint>();
    const lay = (id: string, start: number, span: number) => {
      const kids = children.get(id) ?? [];
      const total = kids.reduce((sum, kid) => sum + weight(kid), 0);
      let at = start;
      for (const kid of kids) {
        const share = (span * weight(kid)) / total;
        const theta = at + share / 2;
        const f = frames.get(kid), d = depth.get(kid);
        if (f && d !== undefined) {
          const r = radii[d];
          places.set(kid, { x: cx + r * Math.sin(theta) - f.width / 2, y: cy - r * Math.cos(theta) - f.height / 2 });
        }
        lay(kid, at, share);
        at += share;
      }
    };
    const first = children.get(centre) ?? [];
    const lead = first[0];
    if (lead === undefined) return new Map();
    const total = first.reduce((sum, kid) => sum + weight(kid), 0);
    const leadShare = (2 * Math.PI * weight(lead)) / total;
    lay(centre, angle(lead) - leadShare / 2, 2 * Math.PI);
    return places;
  },

  // MARK: Tidy Branch

  /** A note's branch straightened: the notes further out from it in columns, each a step away from it on the side it
   *  lies (right of its parent, or left), every note level with the middle of its own notes. The note itself stays. The
   *  centre's notes go out on both sides, each on the side it's on. Stacked notes follow their head. */
  tidyBranch(id: string, content: MapContent, frames: MapFrames): Map<string, MapPoint> {
    const root = frames.get(id), rootRoom = MapLayout.occupied(id, content, frames);
    if (!root || !rootRoom) return new Map();
    const stacked = new Set([...MapArrange.stacks(content).values()].flat());
    const parents = new Map([...MapLayout.parents(content)].filter(([child]) => !stacked.has(child) && frames.has(child)));
    const children = new Map<string, string[]>();
    for (const [child, parent] of parents) children.set(parent, [...(children.get(parent) ?? []), child]);
    const topLeft = (a: string, b: string) =>
      tupleCompare([frames.get(a)?.y ?? 0, frames.get(a)?.x ?? 0, a], [frames.get(b)?.y ?? 0, frames.get(b)?.x ?? 0, b]);
    for (const [key, kids] of children) children.set(key, kids.sort(topLeft));

    const { siblingGap, childGap } = MapLayout;
    const rooms = new Map<string, MapFrame>();
    /** The room a note takes with its outline and its stack (`MapLayout.occupied`). */
    const room = (n: string): MapFrame => {
      const known = rooms.get(n);
      if (known) return known;
      const r = MapLayout.occupied(n, content, frames) ?? { x: 0, y: 0, width: 0, height: 0 };
      rooms.set(n, r);
      return r;
    };
    const heights = new Map<string, number>();
    const height = (n: string): number => {
      const known = heights.get(n);
      if (known !== undefined) return known;
      const kids = children.get(n) ?? [];
      const block = kids.reduce((sum, k) => sum + height(k), 0) + siblingGap * Math.max(kids.length - 1, 0);
      const h = Math.max(room(n).height, block);
      heights.set(n, h);
      return h;
    };

    const places = new Map<string, MapPoint>();
    /** Lays out `kids` of a note (its room where it now is: `parent`) in a column on one side, centred on it. */
    const column = (kids: string[], parent: MapFrame, right: boolean) => {
      if (!kids.length) return;
      const block = kids.reduce((sum, k) => sum + height(k), 0) + siblingGap * (kids.length - 1);
      let top = midY(parent) - block / 2;
      for (const kid of kids) {
        const f = frames.get(kid);
        if (!f) continue;
        const r = room(kid), slot = height(kid);
        const roomX = right ? maxX(parent) + childGap : parent.x - childGap - r.width;
        const roomY = top + (slot - r.height) / 2;
        places.set(kid, { x: roomX + (f.x - r.x), y: roomY + (f.y - r.y) });
        column(children.get(kid) ?? [], { x: roomX, y: roomY, width: r.width, height: r.height }, right);
        top += slot + siblingGap;
      }
    };
    const kids = children.get(id) ?? [];
    if (id === content.centre) {
      column(kids.filter((k) => (frames.has(k) ? midX(frames.get(k)!) : 0) >= midX(root)), rootRoom, true);
      column(kids.filter((k) => (frames.has(k) ? midX(frames.get(k)!) : 0) < midX(root)), rootRoom, false);
    } else {
      const parentID = parents.get(id);
      const parentFrame = parentID === undefined ? undefined : frames.get(parentID);
      column(kids, rootRoom, parentFrame ? midX(root) >= midX(parentFrame) : true);
    }
    return places;
  },
};
